import { Mention, MentionNotificationContext, MentionContentType } from '../types/mention';
import { NotificationService } from './notificationService';
import { NotificationType, NotificationPriority } from '../types/user';

/**
 * MentionService
 * Handles mention processing and notification delivery
 */
export class MentionService {
  /**
   * Process mentions and send notifications
   * Called after content with mentions is created
   *
   * @param mentions - Array of validated mentions
   * @param context - Notification context
   */
  static async processMentions(
    mentions: Mention[],
    context: MentionNotificationContext
  ): Promise<void> {
    if (!mentions || mentions.length === 0) {
      return;
    }

    try {
      // Get unique user IDs (in case of duplicates)
      // Filter out any undefined/null values
      const validUserIds = mentions
        .map(m => m.userId)
        .filter(userId => userId && typeof userId === 'string');

      const uniqueUserIds = [...new Set(validUserIds)];

      // Send notification to each mentioned user
      for (const userId of uniqueUserIds) {
        await this.sendMentionNotification(userId, context);
      }
    } catch (error) {
      console.error('Error processing mentions:', error);
      // Don't throw - notifications are non-critical
    }
  }

  /**
   * Send mention notification to a user
   */
  private static async sendMentionNotification(
    userId: string,
    context: MentionNotificationContext
  ): Promise<void> {
    const notificationType = this.getNotificationType(context.contentType);
    const title = this.getNotificationTitle(context);
    const message = this.getNotificationMessage(context);
    const actionUrl = this.getActionUrl(context);

    await NotificationService.createNotification({
      userId,
      type: notificationType,
      title,
      message,
      priority: NotificationPriority.NORMAL,
      actionUrl,
      actionLabel: this.getActionLabel(context.contentType),
      data: {
        mentionedBy: context.authorId,
        mentionedByName: context.authorName,
        contentType: context.contentType,
        contentId: context.contentId,
        contentPreview: context.contentPreview,
        targetType: context.targetType,
        targetId: context.targetId,
        conversationId: context.conversationId,
      },
    });
  }

  /**
   * Get notification type based on content type
   */
  private static getNotificationType(contentType: MentionContentType): NotificationType {
    const typeMap: Record<MentionContentType, NotificationType> = {
      comment: 'comment_mention',
      message: 'message_mention',
      note: 'note_mention',
      memory: 'memory_mention',
      feature_request: 'feature_request_mention',
    };
    return typeMap[contentType];
  }

  /**
   * Get notification title
   */
  private static getNotificationTitle(context: MentionNotificationContext): string {
    const titles: Record<MentionContentType, string> = {
      comment: `${context.authorName} mentioned you in a comment`,
      message: `${context.authorName} mentioned you in a message`,
      note: `${context.authorName} mentioned you in a note`,
      memory: `${context.authorName} mentioned you in a memory`,
      feature_request: `${context.authorName} mentioned you in a feature request`,
    };
    return titles[context.contentType];
  }

  /**
   * Get notification message (preview)
   */
  private static getNotificationMessage(context: MentionNotificationContext): string {
    return `"${context.contentPreview}"`;
  }

  /**
   * Get action URL to navigate to the content
   */
  private static getActionUrl(context: MentionNotificationContext): string {
    switch (context.contentType) {
      case 'comment':
        // Navigate to the person profile with comment highlighted
        return `/person/${context.targetId}#comment-${context.contentId}`;

      case 'message':
        // Navigate to the conversation
        return `/messages/${context.conversationId}#message-${context.contentId}`;

      case 'note':
        // Navigate to the person profile notes tab
        return `/person/${context.targetId}?tab=notes#note-${context.contentId}`;

      case 'memory':
        // Navigate to the memory
        return `/person/${context.targetId}/memory/${context.contentId}`;

      case 'feature_request':
        // Navigate to the feature request
        return `/admin/feature-requests/${context.targetId}#comment-${context.contentId}`;

      default:
        return '/';
    }
  }

  /**
   * Get action label for notification button
   */
  private static getActionLabel(contentType: MentionContentType): string {
    const labels: Record<MentionContentType, string> = {
      comment: 'View Comment',
      message: 'Open Conversation',
      note: 'View Note',
      memory: 'Read Memory',
      feature_request: 'View Request',
    };
    return labels[contentType];
  }
}

export const mentionService = MentionService;
